import React, { Dispatch, useEffect, useState } from "react"
import { ReservationProps } from "../../interfaces"
import IInvoice from "../../interfaces/IInvoice"
import IOrder from "../../interfaces/IOrder"
import * as Sentry from "@sentry/react"
import InvoiceDecorator, { INVOICE_STATUSES } from "../../decorators/InvoiceDecorator"
import { STATUSES } from "../../constants"
import Spinner from "../ui/Spinner"
import { formatName } from "../../utils/formatName"
import { useOrder } from "../Admin/Reservations/ReservationHooks/useOrder"
import { useOrderInvoice } from "../Admin/Reservations/ReservationHooks/useOrderInvoice"
import { useReservation } from "../Admin/Reservations/ReservationHooks/useReservation"

interface ComponentProps {
  reservation: ReservationProps | undefined
  order: IOrder | undefined
  children: (renderProps: {
    orderInvoice?: IInvoice
    setInvoice?: React.Dispatch<React.SetStateAction<InvoiceDecorator | undefined>>
  }) => JSX.Element
}

const ReservationInvoiceWrapper = ({ reservation, order: propOrder, children }: ComponentProps) => {
  const [orderInvoice, setOrderInvoice] = useState<Partial<IInvoice>>()
  const { data: orderData } = useOrder({ orderId: reservation?.order_id })
  const order = orderData?.order
  const { data: orderInvoiceRemote, isLoading: isLoadingInvoice } = useOrderInvoice({ orderId: reservation?.order_id })
  const isLoading = isLoadingInvoice

  useEffect(() => {
    if (!orderInvoiceRemote || !order) {
      setDummyInvoice()
    } else {
      setOrderInvoice(orderInvoiceRemote)
    }
  }, [isLoading, order, orderInvoiceRemote])

  const setDummyInvoice = () => {
    let invoiceStatus = INVOICE_STATUSES.DRAFT

    if (order && order.status === "paid") {
      invoiceStatus = INVOICE_STATUSES.PAID
    } else if (reservation?.status === STATUSES.PAID) {
      invoiceStatus = INVOICE_STATUSES.PAID
    }

    setOrderInvoice({
      id: undefined,
      send_date: undefined,
      due_date: reservation?.start_date,
      hash_id: reservation?.hashed_id,
      is_auto_send: false,
      recipient_email: reservation?.user?.camper?.email,
      recipient_phone: reservation?.user?.camper?.phone,
      recipient_name: formatName(reservation?.user?.camper),
      recipient_address: reservation?.user?.camper?.address,
      memo: undefined,
      deleted_at: undefined,
      order_id: order?.id,
      order: order,
      reservation: reservation,
      status: invoiceStatus,
      invoice: undefined,
      is_attempted_auto_charge: false,
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-72 sm:w-96 h-96 sm:h-72">
        <Spinner size={5} />
        <p className="mt-2 text-sm text-gray-500">Loading invoice details...</p>
      </div>
    )
  }

  return children({
    orderInvoice: orderInvoice as IInvoice,
    setInvoice: setOrderInvoice as Dispatch<React.SetStateAction<InvoiceDecorator | undefined>>,
  })
}

export default ReservationInvoiceWrapper
